import { TableColumnsType } from 'antd';
import TableComponent from 'components/Table/TableComponent';
import usePagination from 'hooks/usePagination';
import Content from 'layouts/Content';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { formatCurrency, generateUUID } from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';

interface ITableCustomerRevenueProps {
  loading?: boolean;
  data: any;
  exportExcel?: (columns: any) => void;
}

const TableCustomerRevenue = (
  { data, loading, exportExcel }: ITableCustomerRevenueProps,
  ref: any,
) => {
  const { page, page_size } = usePagination();

  const columns: TableColumnsType = useMemo(
    () => [
      {
        width: 50,
        title: 'STT',
        align: 'center',
        render: (_: any, __: any, index: number) =>
          index + 1 + page_size * (page - 1),
      },
      {
        width: 100,
        title: 'Tháng',
        dataIndex: 'data_month_info',
        align: 'center',
        render: (value: any) => value ?? '-',
      },
      {
        width: 250,
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
      },
      {
        width: 160,
        title: 'Mã khách hàng',
        dataIndex: 'sap_customer_id',
      },
      {
        width: 160,
        title: 'Mã khách hàng (Cha)',
        dataIndex: 'main_customer_id',
      },
      {
        width: 150,
        title: 'Tỉnh thành',
        dataIndex: 'city_name',
      },
      {
        title: 'Hàng công',
        children: [
          {
            width: 80,
            title: 'Hạng thẻ',
            dataIndex: 'rank_name',
            render: (value: string) => value ?? '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 140,
            title: 'Doanh thu công',
            dataIndex: 'proc_process_price_sap',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 140,
            title: 'Doanh thu hao lai',
            dataIndex: 'proc_haolai',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 100,
            title: 'Chiết khấu',
            dataIndex: 'proc_total_discount',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 140,
            title: 'Tổng doanh thu',
            dataIndex: 'proc_total_dt',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            width: 180,
            align: 'right',
            title: 'DT còn lại nâng hạng KH',
            dataIndex: 'proc_rank_up',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
        ],
      },
      {
        title: 'Hàng món',
        children: [
          {
            width: 100,
            title: 'Hạng thẻ',
            dataIndex: 'rank_name',
            render: (value: string) => value ?? '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 140,
            title: 'Doanh thu công',
            dataIndex: 'prod_process_price_sap',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 160,
            title: 'Doanh thu hóa đơn',
            dataIndex: 'prod_final_price',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 100,
            title: 'Chiết khấu',
            dataIndex: 'prod_total_discount',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 130,
            title: 'Tổng doanh thu',
            dataIndex: 'prod_total_dt',
            align: 'right',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
          {
            width: 180,
            title: 'DT còn lại nâng hạng KH',
            align: 'right',
            dataIndex: 'prod_rank_up',
            render: (value: number) =>
              value ? formatCurrency({ value }) : '-',
          },
        ],
      },
    ],
    [page, page_size],
  );

  useImperativeHandle(ref, () => ({
    handleExportExcel,
  }));

  const handleExportExcel = () => {
    exportExcel && exportExcel(columns);
  };

  return (
    <Content>
      <TableComponent
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data.items}
        rowKey={(record) => record?.id || generateUUID()}
        loading={loading}
        pagination={{
          current: data.pageInfo?.currentPage,
          pageSize: data.pageInfo?.itemsPerPage,
          total: data.pageInfo?.totalItems,
          onChange: HocChangePagination(),
        }}
      />
    </Content>
  );
};

export default forwardRef(TableCustomerRevenue);
